import { ArrowBack, LaunchRounded } from "@mui/icons-material";
import { Box, Button, Chip, CircularProgress, Divider, Typography } from "@mui/material";
import { useState } from "react";
import PurchaseDetail from "@/components/finance/PurchaseDetail";
import { InvoiceDetail } from "@/components/usersOrgs/GroupInvoicesPanel";
import { SOURCE_TYPE_INVOICE, SOURCE_TYPE_PURCHASE, sourceTypeLabel } from "@/constants/accounting";
import { useJournalEntry } from "@/hooks/useAccounting";
import type { JournalEntryDetail as Entry, JournalEntryLine } from "@/interfaces/accounting";
import { formatDate, formatDateTime } from "@/utils/date-format";

const money = (v: string | null) =>
  v == null
    ? "—"
    : Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface StatusChip {
  label: string;
  color: "default" | "success" | "warning";
}
const DRAFT: StatusChip = { label: "Draft", color: "default" };
const STATUS: Record<number, StatusChip> = {
  0: DRAFT,
  1: { label: "Posted", color: "success" },
  2: { label: "Needs review", color: "warning" },
};
const statusChip = (s: number): StatusChip => STATUS[s] ?? DRAFT;
const ENTRY_TYPE: Record<number, string> = {
  0: "Normal",
  1: "Adjustment",
  2: "Reversal",
};

/** A labelled fact in the header grid. */
function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: 11,
          letterSpacing: "0.6px",
          textTransform: "uppercase",
          color: "var(--color-graphite)",
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ fontSize: 14, fontWeight: 500 }}>{children}</Box>
    </Box>
  );
}

/**
 * The double-entry table. Debits and credits get their own columns so the entry reads the way an
 * accountant expects — amount in the column that names its side, one account per row, totals ruled
 * off at the bottom and required to agree.
 */
function LinesTable({ entry }: { entry: Entry }) {
  const border = "1px solid var(--color-card-border)";
  const balanced = Number(entry.totalDebit) === Number(entry.totalCredit);

  const cell = {
    padding: "10px 14px",
    borderBottom: border,
    fontSize: 13.5,
    verticalAlign: "top" as const,
  };
  const num = {
    ...cell,
    textAlign: "right" as const,
    fontVariantNumeric: "tabular-nums" as const,
    whiteSpace: "nowrap" as const,
    width: 140,
  };
  const head = {
    padding: "9px 14px",
    borderBottom: border,
    fontSize: 11,
    letterSpacing: "0.6px",
    textTransform: "uppercase" as const,
    color: "var(--color-graphite)",
    fontWeight: 600,
  };

  return (
    <Box sx={{ border, borderRadius: "var(--radius-card)", overflow: "hidden" }}>
      <Box sx={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 620 }}>
          <thead>
            <tr style={{ background: "var(--color-bone)" }}>
              <th style={{ ...head, textAlign: "left" }}>Account</th>
              <th style={{ ...head, textAlign: "right", width: 140 }}>Debit</th>
              <th style={{ ...head, textAlign: "right", width: 140 }}>Credit</th>
            </tr>
          </thead>
          <tbody>
            {entry.lines.map((l: JournalEntryLine) => {
              const isDebit = l.drCr === "debit";
              return (
                <tr key={l.id}>
                  <td style={cell}>
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                      {l.qbAccountNumber && (
                        <Box
                          component="span"
                          sx={{
                            fontVariantNumeric: "tabular-nums",
                            color: "var(--color-graphite)",
                            fontSize: 12.5,
                          }}
                        >
                          {l.qbAccountNumber}
                        </Box>
                      )}
                      <Box component="span" sx={{ fontWeight: 500 }}>
                        {l.qbAccountName ?? (
                          <Box component="span" sx={{ color: "var(--color-warning, #b26a00)" }}>
                            Unresolved account
                          </Box>
                        )}
                      </Box>
                    </Box>
                    {/* Our logical account — what the posting rule actually named. */}
                    <Typography sx={{ fontSize: 11.5, color: "var(--color-graphite)", mt: 0.25 }}>
                      {l.accountKey ?? "—"}
                    </Typography>
                  </td>
                  <td style={num}>{isDebit ? money(l.amount) : ""}</td>
                  <td style={num}>{isDebit ? "" : money(l.amount)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ background: "var(--color-bone)" }}>
              <td style={{ ...cell, borderBottom: "none", fontWeight: 600 }}>
                Total
                {!balanced && (
                  <Box
                    component="span"
                    sx={{ ml: 1, fontSize: 12, fontWeight: 500, color: "#b3261e" }}
                  >
                    does not balance
                  </Box>
                )}
              </td>
              <td style={{ ...num, borderBottom: "none", fontWeight: 700 }}>
                {money(entry.totalDebit)}
              </td>
              <td style={{ ...num, borderBottom: "none", fontWeight: 700 }}>
                {money(entry.totalCredit)}
              </td>
            </tr>
          </tfoot>
        </table>
      </Box>
    </Box>
  );
}

/**
 * Journal entry show page. Reached from the Journal No in the blotter.
 *
 * The source links straight through to its own detail view — the shared InvoiceDetail for an
 * invoice, PurchaseDetail for a credit purchase — rendered in place with a back link returning
 * here. Same swap-in-panel pattern Finance uses, so no route and no duplicate views.
 */
export default function JournalEntryDetail({
  journalEntryId,
  onBack,
  backLabel = "Journal Entries",
}: {
  journalEntryId: number;
  onBack: () => void;
  backLabel?: string;
}) {
  const { data: entry, isLoading } = useJournalEntry(journalEntryId);
  const [showSource, setShowSource] = useState(false);

  if (showSource && entry?.sourceId != null) {
    const back = `Journal ${entry.journalNo}`;
    if (entry.sourceType === SOURCE_TYPE_INVOICE && entry.sourceGroupId != null) {
      return (
        <InvoiceDetail
          groupId={entry.sourceGroupId}
          invoiceId={entry.sourceId}
          onBack={() => setShowSource(false)}
          backLabel={back}
        />
      );
    }
    if (entry.sourceType === SOURCE_TYPE_PURCHASE) {
      return (
        <PurchaseDetail
          purchaseId={entry.sourceId}
          onBack={() => setShowSource(false)}
          backLabel={back}
        />
      );
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress aria-label="Loading" size={28} />
      </Box>
    );
  }
  if (!entry) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={onBack} sx={{ textTransform: "none" }}>
          {backLabel}
        </Button>
        <Typography sx={{ mt: 2 }}>Journal entry not found.</Typography>
      </Box>
    );
  }

  const status = statusChip(entry.status);
  // An invoice needs its group to open; a purchase is reachable by id alone.
  const canOpenSource =
    entry.sourceId != null &&
    ((entry.sourceType === SOURCE_TYPE_INVOICE && entry.sourceGroupId != null) ||
      entry.sourceType === SOURCE_TYPE_PURCHASE);

  return (
    <Box sx={{ fontFamily: "var(--font-lato)" }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={onBack}
        sx={{ textTransform: "none", color: "var(--color-graphite)", mb: 2 }}
      >
        {backLabel}
      </Button>

      {/* Header — identity and status, then the facts that explain the entry. */}
      <Box
        sx={{
          border: "1px solid var(--color-card-border)",
          borderRadius: "var(--radius-card)",
          p: { xs: 2, md: 3 },
          mb: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            mb: 2.5,
          }}
        >
          <Typography sx={{ fontSize: 22, fontWeight: 700, letterSpacing: "0.3px" }}>
            {entry.journalNo}
          </Typography>
          <Chip size="small" variant="outlined" label={status.label} color={status.color} />
          {entry.entryType !== 0 && (
            <Chip size="small" variant="outlined" label={ENTRY_TYPE[entry.entryType]} />
          )}
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
            gap: 2.5,
          }}
        >
          <Fact label="Accounting date">{formatDate(entry.accountingDate)}</Fact>
          <Fact label="Posted date">{formatDate(entry.asOnDate)}</Fact>
          <Fact label="Event">
            {entry.reasonLabel}
            <Typography sx={{ fontSize: 11.5, color: "var(--color-graphite)" }}>
              {entry.reasonCode}
            </Typography>
          </Fact>
          <Fact label="Source">
            {canOpenSource ? (
              <Button
                variant="text"
                endIcon={<LaunchRounded sx={{ fontSize: 15 }} />}
                onClick={() => setShowSource(true)}
                sx={{
                  textTransform: "none",
                  p: 0,
                  minWidth: 0,
                  fontWeight: 600,
                  fontSize: 14,
                  color: "var(--color-rodeo-black)",
                }}
              >
                {sourceTypeLabel(entry.sourceType)} {entry.sourceReference}
              </Button>
            ) : (
              <>
                {sourceTypeLabel(entry.sourceType)}
                {entry.sourceId != null && (
                  <Typography sx={{ fontSize: 11.5, color: "var(--color-graphite)" }}>
                    #{entry.sourceId}
                  </Typography>
                )}
              </>
            )}
          </Fact>
          <Fact label="Organization">{entry.organizationName ?? "—"}</Fact>
          <Fact label="Group">{entry.groupName ?? "—"}</Fact>
          <Fact label="Recorded">{formatDateTime(entry.createdAt)}</Fact>
          <Fact label="Actioned by">{entry.actorName ?? "System"}</Fact>
        </Box>

        {entry.memo && (
          <>
            <Divider sx={{ my: 2.5 }} />
            <Fact label="Memo">
              <Typography sx={{ fontSize: 14, fontWeight: 400 }}>{entry.memo}</Typography>
            </Fact>
          </>
        )}

        {entry.accountingDate !== entry.asOnDate && (
          <Typography sx={{ fontSize: 12, color: "var(--color-graphite)", mt: 2 }}>
            Back-dated — this entry belongs to {formatDate(entry.accountingDate)} but was posted on{" "}
            {formatDate(entry.asOnDate)}.
          </Typography>
        )}
      </Box>

      <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.5px", mb: 1.25 }}>
        LINES
      </Typography>
      <LinesTable entry={entry} />

      {entry.status === 2 && (
        <Typography sx={{ fontSize: 12.5, color: "var(--color-graphite)", mt: 1.5 }}>
          One or more accounts don't resolve to a QuickBooks account, so this entry is held back
          from export until that's fixed.
        </Typography>
      )}
    </Box>
  );
}
